import React from 'react';
import type {
  AfcSubmissionCandidateOpportunity,
  AfcSubmission,
  Candidate,
  BarredLocation,
  Taxonomy,
} from '@axiom/validation';
import {
  BarredLocationsConst,
  Education as EducationConst,
} from '@axiom/const';
import { CandidateYearsOfExperienceModel } from '@axiom/ui-models';

import RateIcon from '../../../public/static/svg/rate-icon.svg';
import AdmissionsIcon from '../../../public/static/svg/admissions-icon.svg';
import EducationIcon from '../../../public/static/svg/education-icon.svg';
import ExperienceIcon from '../../../public/static/svg/experience-icon.svg';
import { CandidateProposedRateUtil } from '../../../utils/candidate-proposed-rate-util';
import { Grid } from '../../layout/Grid/Grid';
import { GridColumn } from '../../layout/Grid/GridColumn';
import { GridRow } from '../../layout/Grid/GridRow';
import { Gutter } from '../../layout/Gutter/Gutter';
import { Layout } from '../../layout/Layout/Layout';
import { LayoutItem } from '../../layout/Layout/LayoutItem';
import { Paragraph } from '../../content/Paragraph/Paragraph';
import { ParagraphHeader } from '../../content/ParagraphHeader/ParagraphHeader';
import { CandidateSubmissionRate } from '../CandidateSubmissionRate/CandidateSubmissionRate';
import { BusinessTitlesUtil } from '../../../utils/business-titles-util';
import { TaxonomyUtil } from '../../../utils/taxonomy-util';

export type CandidateHighlightsExperienceSummaryCandidate = {
  id?: Candidate['id'];
  yearsOfExperience?: Candidate['yearsOfExperience'];
  barredLocations?: Array<{ name: BarredLocation['name'] }> | null;
  lawDegreeSchools?: Candidate['lawDegreeSchools'];
  businessTitle?: Candidate['businessTitle'];
};

export type CandidateHighlightExperienceSummaryTypes = {
  isDownloading?: boolean;
  candidate: CandidateHighlightsExperienceSummaryCandidate;
  submission?: AfcSubmission | null;
  condensed?: boolean;
  taxonomy: Taxonomy;
};

const calculateRate = (
  submission: AfcSubmission | null,
  candidateId: Candidate['id'],
  candidateOpportunity: AfcSubmissionCandidateOpportunity | null
) => {
  if (
    submission &&
    candidateOpportunity &&
    CandidateProposedRateUtil.shouldDisplayRate(candidateOpportunity)
  ) {
    return (
      <CandidateSubmissionRate
        candidateId={candidateId}
        submission={submission}
      />
    );
  }

  return null;
};

const calculateColumnWidth = (
  numberOfSummaryItems: number,
  condensed: boolean
) => {
  if (numberOfSummaryItems === 1) {
    return 12;
  } else if (condensed && numberOfSummaryItems === 4) {
    return 3;
  } else if (numberOfSummaryItems % 2 === 0) {
    return 6;
  }
  return 4;
};

export const CandidateHighlightExperienceSummary = ({
  isDownloading,
  candidate,
  submission = null,
  condensed = false,
  taxonomy,
}: CandidateHighlightExperienceSummaryTypes) => {
  // 1. Derived data from candidate
  const filteredLawDegreeSchools = [
    ...new Set(
      candidate.lawDegreeSchools?.map(education => {
        const degree = education.degree
          ? `, ${EducationConst.DEGREES[education.degree]}`
          : '';
        return `${education.institution}${degree}`;
      })
    ),
  ];
  const filteredBarredLocations = candidate.barredLocations?.filter(
    admission => admission.name !== BarredLocationsConst.Names.NotAdmitted
  );
  const formattedLegalRole = BusinessTitlesUtil.locationConversion({
    businessTitle:
      TaxonomyUtil.getBusinessTitle(taxonomy, candidate.businessTitle ?? null)
        ?.displayName ?? null,
    candidate: candidate as Candidate,
  });

  // 2. Submission-related data
  const candidateOpportunity =
    submission?.positions[0]?.candidateOpportunities.find(candidateOpp => {
      return candidateOpp.candidate.id === candidate.id;
    });
  const calculatedRate = candidate.id
    ? calculateRate(
        submission ?? null,
        candidate.id,
        candidateOpportunity ?? null
      )
    : null;

  // 3. Existence checks
  const hasRate = !!calculatedRate;
  const hasYearsOfExperience =
    candidate.yearsOfExperience !== null &&
    candidate.yearsOfExperience !== undefined &&
    candidate.yearsOfExperience >= 0;
  const hasLawSchools = (candidate.lawDegreeSchools?.length ?? 0) > 0;
  const hasBarredLocations = (filteredBarredLocations?.length ?? 0) > 0;
  const hasLegalRole = !!formattedLegalRole;

  // 4. Layout calculation
  const numberOfSummaryItems = [
    hasRate,
    hasYearsOfExperience,
    hasLawSchools,
    hasBarredLocations,
    hasLegalRole,
  ].filter(Boolean).length;
  const columnWidth = calculateColumnWidth(numberOfSummaryItems, condensed);

  return (
    <Grid
      textAlign="justified"
      stackable={!isDownloading}
      name="highlights-experience-summary"
    >
      <GridRow>
        {hasRate ? (
          <GridColumn
            verticalAlign="top"
            largeScreenWidth={columnWidth}
            tabletWidth={columnWidth}
            mobileWidth={isDownloading ? columnWidth : 12}
            name="highlight-hourly-rate-section"
          >
            <Layout horizontalGutter="8px">
              <LayoutItem>
                <RateIcon width="44px" height="44px" />
              </LayoutItem>
              <LayoutItem>
                <ParagraphHeader name="RATE">Rate</ParagraphHeader>
                <Paragraph name="highlight-hourly-rate">
                  {calculatedRate}
                </Paragraph>
                <Gutter bottom="16px" />
              </LayoutItem>
            </Layout>
          </GridColumn>
        ) : null}
        {hasYearsOfExperience ? (
          <GridColumn
            verticalAlign="top"
            largeScreenWidth={columnWidth}
            tabletWidth={columnWidth}
            mobileWidth={isDownloading ? columnWidth : 12}
            name="highlight-experience-section"
          >
            <Layout horizontalGutter="8px">
              <LayoutItem>
                <ExperienceIcon width="44px" height="44px" />
              </LayoutItem>
              <LayoutItem>
                <ParagraphHeader>Experience</ParagraphHeader>
                <Paragraph name="highlight-experience">
                  {`${new CandidateYearsOfExperienceModel(
                    candidate.yearsOfExperience
                  ).getYearsOfExperienceRange()} years`}
                </Paragraph>
                <Gutter bottom="16px" />
              </LayoutItem>
            </Layout>
          </GridColumn>
        ) : null}
        {hasLawSchools ? (
          <GridColumn
            verticalAlign="top"
            largeScreenWidth={columnWidth}
            tabletWidth={columnWidth}
            mobileWidth={isDownloading ? columnWidth : 12}
            name="highlight-education-section"
          >
            <Layout horizontalGutter="8px">
              <LayoutItem>
                <EducationIcon width="44px" height="44px" />
              </LayoutItem>
              <LayoutItem>
                <ParagraphHeader>Education</ParagraphHeader>
                <Paragraph name="highlight-education">
                  {filteredLawDegreeSchools.map((school, index) => (
                    <div key={`admission-${index + 1}`}>{school}</div>
                  ))}
                </Paragraph>
                <Gutter bottom="16px" />
              </LayoutItem>
            </Layout>
          </GridColumn>
        ) : null}
        {hasBarredLocations ? (
          <GridColumn
            verticalAlign="top"
            largeScreenWidth={columnWidth}
            tabletWidth={columnWidth}
            mobileWidth={isDownloading ? columnWidth : 12}
            name="highlight-admissions-section"
          >
            <Layout horizontalGutter="8px">
              <LayoutItem>
                <AdmissionsIcon width="44px" height="44px" />
              </LayoutItem>
              <LayoutItem>
                <ParagraphHeader>Admissions</ParagraphHeader>
                <Paragraph name="highlight-admissions">
                  {candidate.barredLocations?.map(admission => (
                    <div key={`admission-${admission.name}`}>
                      {`${admission.name}`}
                    </div>
                  ))}
                </Paragraph>
                <Gutter bottom="16px" />
              </LayoutItem>
            </Layout>
          </GridColumn>
        ) : null}
        {hasLegalRole ? (
          <GridColumn
            verticalAlign="top"
            largeScreenWidth={columnWidth}
            tabletWidth={columnWidth}
            mobileWidth={isDownloading ? columnWidth : 12}
            name="highlight-legal-role-section"
          >
            <Layout horizontalGutter="8px">
              <LayoutItem>
                <AdmissionsIcon width="44px" height="44px" />
              </LayoutItem>
              <LayoutItem>
                <ParagraphHeader>Legal Role</ParagraphHeader>
                <Paragraph name="highlight-legal-role">
                  {formattedLegalRole}
                </Paragraph>
                <Gutter bottom="16px" />
              </LayoutItem>
            </Layout>
          </GridColumn>
        ) : null}
      </GridRow>
    </Grid>
  );
};
